import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BRANDS, type Brand, type LogoTone } from '../data/brands';

export function Brands() {
  return (
    <section id="brands" className="border-line border-b">
      <div className="mx-auto max-w-6xl px-6 py-24 md:py-32">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="marketing-label text-clay">
              Our partners
            </p>
            <h2 className="font-display mt-4 text-4xl leading-tight md:text-5xl">
              {BRANDS.length} brands.
              <span className="text-rust italic"> Full focus.</span>
            </h2>
          </div>
          <p className="text-clay max-w-sm">
            We work with consumer AI and SaaS brands, building influencer campaigns that
            connect their products with the right audiences.
          </p>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-2">
          {BRANDS.map((brand) => (
            <BrandCard key={brand.name} brand={brand} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BrandCard({ brand }: { brand: Brand }) {
  return (
    <article className="border-line bg-cream hover:border-clay/40 flex flex-col justify-between rounded-xl border p-6 transition-colors duration-200 md:p-8">
      <div>
        <div className="flex items-center justify-between gap-4">
          <LogoChip src={brand.logo} tone={brand.logoTone} name={brand.name} />
          <span className="text-clay text-xs">{brand.tag}</span>
        </div>

        <h3 className="font-display mt-8 text-2xl leading-snug md:text-3xl">
          {brand.headline}
        </h3>
        <p className="text-clay mt-5">{brand.description}</p>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
        <span className="text-sm font-medium">{brand.name}</span>

        <div className="flex items-center gap-5">
          {/* The brief is the primary action here: this grid is read by
              creators deciding whether to take a deal far more often than
              by anyone shopping for the product. */}
          <Link
            to={`/brand/${brand.slug}`}
            className="text-ink hover:text-rust group inline-flex items-center gap-1.5 text-sm font-medium transition-colors duration-200"
          >
            Read the brief
            <ArrowUpRight
              className="size-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              aria-hidden
            />
          </Link>

          <a
            href={brand.url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-clay hover:text-ink text-sm transition-colors duration-200"
          >
            Visit
          </a>
        </div>
      </div>
    </article>
  );
}

/**
 * `bare` logos are app icons that already ship their own background and corner
 * radius, so they get no plate — putting one behind them would frame a frame.
 */
function LogoChip({ src, tone, name }: { src: string; tone: LogoTone; name: string }) {
  const plate =
    tone === 'bare'
      ? ''
      : tone === 'dark'
        ? 'bg-ink ring-1 ring-ink p-3'
        : 'bg-cream-2 ring-1 ring-line p-3';

  return (
    <div className={`flex size-14 items-center justify-center rounded-xl ${plate}`}>
      <img
        src={src}
        alt={`${name} logo`}
        className={`block h-full w-full object-contain ${tone === 'bare' ? 'rounded-xl' : ''}`}
        loading="lazy"
      />
    </div>
  );
}
